import type { ScheduleEntry, WorkPhase } from '@myapp/contract'

/**
 * The instants a phase-closing sheet may correct, resolved from the entry plus the stamp the
 * server is about to write.
 *
 * The worker never sees a duration field. He moves these points and reads the derived minutes
 * underneath them; the server re-derives the same minutes from whatever he submits.
 */
export interface SessionTimes {
  travelStartedAt: string | null
  actualWorkStartsAt: string | null
  actualEndsAt: string | null
  departureEndedAt: string | null
}

/** "HH:MM" local wall clock — used for the read-only summaries, never as an input. */
export function formatTimeDe(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

/** The current minute as an ISO instant — the default a closing action submits. */
export function nowInstant(): string {
  const now = new Date()
  now.setSeconds(0, 0)
  return now.toISOString()
}

/**
 * Which instants a given closing action is allowed to touch.
 *
 * Sign-off reviews the whole session it is about to file. Closing the departure reviews only the
 * departure, because the sign-off sheet ran before that instant existed.
 */
export function correctableFields(phase: WorkPhase | null): Array<keyof SessionTimes> {
  if (phase === 'ABFAHRT') return ['departureEndedAt']
  return ['travelStartedAt', 'actualWorkStartsAt', 'actualEndsAt']
}

/** The instants of a session as the sheet starts out: recorded values plus the pending stamp. */
export function sessionTimesFrom(
  schedule: Pick<
    ScheduleEntry,
    'travelStartedAt' | 'actualWorkStartsAt' | 'actualEndsAt' | 'departureEndedAt'
  >,
  pending: { field: keyof SessionTimes; value: string },
): SessionTimes {
  return {
    travelStartedAt: schedule.travelStartedAt,
    actualWorkStartsAt: schedule.actualWorkStartsAt,
    actualEndsAt: schedule.actualEndsAt,
    departureEndedAt: schedule.departureEndedAt,
    [pending.field]: pending.value,
  }
}

/**
 * Only the fields the technician actually moved travel to the server. Accepting a value sends
 * nothing for it, which is what keeps "accepted the system value" out of the audit entirely.
 */
export function changedFields(
  initial: SessionTimes,
  current: SessionTimes,
  fields: Array<keyof SessionTimes>,
): Partial<Record<keyof SessionTimes, string>> {
  const changed: Partial<Record<keyof SessionTimes, string>> = {}
  for (const field of fields) {
    const value = current[field]
    if (value && value !== initial[field]) changed[field] = value
  }
  return changed
}
